import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RmBodyZone,
  Textarea,
  useToast,
} from "randmarcomps";
import { CreateOpportunityInput, usePutV4ManufacturerByRouteManufacturerIdOpportunityMutation } from "randmar-api-client";
import RmPageHeader from "../components/layout/RmPageHeader";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { setUserSelectedOpportunity } from "../utils/LocalStorageUtils";
import withRoot from "../withRoot";

interface IProps {
  applicationId: string;
}

const CreateOpportunity: React.FC<IProps> = ({ applicationId }) => {
  const { toast } = useToast();
  const [addOpportunity, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdOpportunityMutation();

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bidNumber, setBidNumber] = useState("");
  const [bidTitle, setBidTitle] = useState("");
  const [quoteOnly, setQuoteOnly] = useState(false);
  const [description, setDescription] = useState("");
  const [defaultResellers, setDefaultResellers] = useState("");


  const handleConfirmRequest = async () => {
    const opportunityData: CreateOpportunityInput = {
      BidNumber: bidNumber,
      BidTitle: bidTitle,
      StartDate: startDate,
      EndDate: endDate,
      QuoteOnly: quoteOnly,
      Description: description,
      DefaultResellers: defaultResellers,
    };

    try {
      await addOpportunity({ routeManufacturerId: applicationId, createOpportunityInput: opportunityData }).unwrap();
      toast({ variant: "success", title: "Success!", description: "Opportunity created." });
      setUserSelectedOpportunity(bidNumber);
      setOpen(true);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Opportunity could not be created." });
    }
  }

  const handleCancel = () => {
    setOpen(false);
    setStartDate("");
    setEndDate("");
    setBidNumber("");
    setBidTitle("");
    setQuoteOnly(false);
    setDescription("");
    setDefaultResellers("");
  }

  const propertyChange = (propertyName: string, newValue: string) => {
    switch (propertyName) {
      case "startDate": setStartDate(newValue);
        break;
      case "endDate": setEndDate(newValue);
        break;
      case "bidNumber": setBidNumber(newValue);
        break;
      case "bidTitle": setBidTitle(newValue);
        break;
      case "quoteOnly": setQuoteOnly(newValue === 'true');
        break;
      case "description": setDescription(newValue);
        break;
      case "defaultResellers":
        {
          let newDefaultResellersValue = defaultResellers;
          if (defaultResellers.includes(newValue))
            newDefaultResellersValue = newDefaultResellersValue.replace(newValue + ",", "");
          else
            newDefaultResellersValue = newDefaultResellersValue + newValue + ",";

          setDefaultResellers(newDefaultResellersValue);
        }
        break;
    }
  }

  return (
    <>
      <RmPageHeader
        title="Create Opportunity"
        subtitle="Capture basic bid details and optional reseller defaults before launching the opportunity."
      />

      <RmBodyZone className="space-y-8 pb-10">
        <div className="w-full space-y-8">
          <Card className="rounded-2xl border border-border shadow-sm">
            <CardHeader className="space-y-2">
              <CardTitle>Create new opportunity</CardTitle>
              <p className="text-sm text-muted-foreground">Basic information</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="bidNumber">Opportunity number</Label>
                  <Input
                    id="bidNumber"
                    value={bidNumber}
                    onChange={(e) => propertyChange("bidNumber", e.target.value)}
                    className="h-11 rounded-xl"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="bidTitle">Opportunity title</Label>
                  <Input
                    id="bidTitle"
                    value={bidTitle}
                    onChange={(e) => propertyChange("bidTitle", e.target.value)}
                    className="h-11 rounded-xl"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="startDate">Start date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => propertyChange("startDate", e.target.value)}
                    className="h-11 rounded-xl"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="endDate">End date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => propertyChange("endDate", e.target.value)}
                    className="h-11 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="quoteOnly"
                  checked={quoteOnly}
                  onCheckedChange={(checked) => setQuoteOnly(Boolean(checked))}
                />
                <Label htmlFor="quoteOnly" className="cursor-pointer text-[15px] text-muted-foreground">
                  Quote only
                </Label>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => propertyChange("description", e.target.value)}
                  rows={4}
                  placeholder="Any specific information like end-user requirements or minimum ordering number, etc."
                />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-border shadow-sm">
            <CardHeader className="space-y-2">
              <CardTitle>Default resellers (optional)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Selecting one or more options adds all qualified resellers from the corresponding margin packages.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTitle>Targeted resellers</AlertTitle>
                <AlertDescription>
                  If you don’t select default resellers, you’ll need to manually add them later (search by reseller and use Actions).
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                {[
                  { value: "Exclusive", label: "Exclusive - Qualified resellers in the Exclusive package" },
                  { value: "E-Commerce", label: "E‑Commerce - Qualified resellers in the E‑Commerce package" },
                  { value: "Commercial", label: "Commercial - Qualified resellers in the Commercial package" },
                  { value: "Academy", label: "Academy - Qualified Academy resellers in the Buying Group package" },
                  { value: "Novexco", label: "Novexco - Qualified Novexco resellers in the Buying Group package" },
                  { value: "COOPSCO", label: "COOPSCO - Qualified COOPSCO resellers in the Buying Group package" },
                  { value: "Cantrex", label: "Cantrex - Qualified Cantrex resellers in the Buying Group package" },
                  { value: "Jean Coutu", label: "Jean Coutu - Qualified Jean Coutu resellers in the Buying Group package" },
                  { value: "Starter", label: "Starter - Qualified resellers in the Starter package" },
                ].map((option) => (
                  <div key={option.value} className="flex items-start gap-2">
                    <Checkbox
                      id={`defaultResellers-${option.value}`}
                      checked={defaultResellers.includes(option.value)}
                      onCheckedChange={() => propertyChange("defaultResellers", option.value)}
                    />
                    <Label
                      htmlFor={`defaultResellers-${option.value}`}
                      className="cursor-pointer text-[15px] text-muted-foreground"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              size="lg"
              className="rounded-xl"
              onClick={handleConfirmRequest}
              disabled={!startDate || !endDate || !bidTitle || !bidNumber || startDate > endDate || isLoading}
            >
              Create opportunity
            </Button>
          </div>
        </div>
      </RmBodyZone>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Success!</DialogTitle>
            <DialogDescription>
              Your opportunity has been successfully created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <AlertTitle>And then?</AlertTitle>
              <AlertDescription>
                Now add products to this opportunity (search by product and use Actions). If you didn’t select default resellers,
                you’ll add them manually the same way.
              </AlertDescription>
            </Alert>

            <p className="text-sm text-muted-foreground">Where to next?</p>
            <Button asChild className="rounded-xl">
              <Link to={Path.opportunity(applicationId, bidNumber)}>Go to {bidNumber}</Link>
            </Button>
          </div>
          <DialogFooter className="flex justify-between pt-4">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to={Path.opportunities(applicationId)}>View all opportunities</Link>
            </Button>
            <Button onClick={handleCancel} variant="outline">
              Create another Opportunity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default withRoot(withManufacturer(CreateOpportunity));
